import React, { useState, useEffect, useCallback } from "react"
import { useCombobox } from "downshift"

const NewSelectBox= ({
  options = [],
  onChange,
  value,
  creatable = false,
  disabled = false,
  onInputChange,
  placeholder = "Select...",
  noOptionsMessage = () => "No options found",
  loadingMessage = () => "Loading...",
  isLoading = false,
}) => {
  const [inputItems, setInputItems] = useState(options)
  const [inputValue, setInputValue] = useState("")

  useEffect(() => {
    setInputItems(options)
  }, [options])

  const handleFilter = useCallback(
    (filter) => {
      const filtered = options.filter((item) => item.label.toLowerCase().includes(filter.toLowerCase()))
      setInputItems(filtered)

      if (creatable && filter && !filtered.length) {
        setInputItems([{ label: `Create "${filter}"`, value: filter }])
      }
    },
    [options, creatable],
  )

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    selectItem,
  } = useCombobox({
    items: inputItems,
    selectedItem: value,
    onSelectedItemChange: ({ selectedItem }) => {
      onChange(selectedItem || null)
      if (selectedItem) {
        setInputValue(selectedItem.label)
      }
    },
    onInputValueChange: ({ inputValue }) => {
      setInputValue(inputValue || "")
      handleFilter(inputValue || "")
      onInputChange?.(inputValue || "")
    },
    itemToString: (item) => item?.label || "",
  })

  const handleCreateOption = () => {
    if (creatable && inputValue) {
      const newOption = { label: inputValue, value: inputValue }
      onChange(newOption)
      setInputItems([...options, newOption])
      selectItem(newOption)
    }
  }

  return (
    <div className="relative w-full">
      <div className="relative">
        <input
          {...getInputProps({
            disabled,
            placeholder,
            onKeyDown: (event) => {
              if (event.key === "Enter" && creatable && inputValue && !inputItems.length) {
                event.preventDefault()
                handleCreateOption()
              }
            },
          })}
          className={`w-full px-4 py-2 text-sm border rounded-md ${
            disabled ? "bg-gray-100" : "bg-white"
          } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
        />
        <button
          type="button"
          {...getToggleButtonProps()}
          className="absolute inset-y-0 right-0 flex items-center px-2"
          aria-label="toggle menu"
        >
          <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="none" stroke="currentColor">
            <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <ul
        {...getMenuProps()}
        className={`absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg ${
          !(isOpen && (inputItems.length || creatable)) && "hidden"
        } max-h-60 overflow-auto`}
      >
        {isOpen && (
          <>
            {isLoading ? (
              <li className="px-4 py-2 text-sm text-gray-500">{loadingMessage()}</li>
            ) : inputItems.length === 0 ? (
              <li className="px-4 py-2 text-sm text-gray-500">{noOptionsMessage()}</li>
            ) : (
              inputItems.map((item, index) => (
                <li
                  key={`${item.value}`}
                  {...getItemProps({ item, index })}
                  className={`px-4 py-2 text-sm cursor-pointer ${
                    highlightedIndex === index ? "bg-blue-50 text-blue-700" : "text-gray-900 hover:bg-gray-50"
                  } ${selectedItem === item ? "font-medium" : ""}`}
                >
                  {item.label}
                </li>
              ))
            )}
            {creatable && inputValue && !inputItems.length && (
              <li
                className="px-4 py-2 text-sm cursor-pointer text-blue-600 hover:bg-blue-50"
                onClick={handleCreateOption}
              >
                Create "{inputValue}"
              </li>
            )}
          </>
        )}
      </ul>
    </div>
  )
}

export default React.memo(NewSelectBox)

